/**
 * CaseManagerSelector
 *
 * A compact dropdown component for selecting a case manager for a matter.
 * Auto-calls API on selection change - no modal interruption.
 * Tasks are automatically reassigned when case manager changes.
 */

import { User, UserMinus } from 'lucide-react';
import { useState } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  type CaseManagerUser,
  useAssignCaseManagerMutation,
  useChangeCaseManagerMutation,
  useRemoveCaseManagerMutation,
} from '@/services/api/caseManagerService';
import { toastWithProgress } from '@/utils/toast';

interface CaseManagerSelectorProps {
  /** The matter ID */
  matterId: string;
  /** Currently assigned case manager ID */
  currentCaseManagerId?: string | null;
  /** Currently assigned case manager display info */
  currentCaseManager?: { id: string; display_name: string } | null;
  /** List of available case managers for this firm */
  caseManagers: CaseManagerUser[];
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Compact mode for Initial Review modal */
  compact?: boolean;
  /** Callback when case manager is successfully assigned/changed/removed */
  onSuccess?: () => void;
  /**
   * Override the auto_task_assignment value when assigning/changing CM.
   * If provided, this value is used instead of the CM's case_auto_assign setting.
   * Use this when the parent has its own toggle for the auto-assign setting.
   */
  autoTaskAssignmentOverride?: boolean;
}

export const CaseManagerSelector = ({
  matterId,
  currentCaseManagerId,
  currentCaseManager,
  caseManagers,
  disabled = false,
  compact = false,
  onSuccess,
  autoTaskAssignmentOverride,
}: CaseManagerSelectorProps) => {
  const [isUpdating, setIsUpdating] = useState(false);

  // API mutations
  const [assignCaseManager] = useAssignCaseManagerMutation();
  const [changeCaseManager] = useChangeCaseManagerMutation();
  const [removeCaseManager] = useRemoveCaseManagerMutation();

  // Check if current case manager is still in the pool
  const hasCurrentCaseManager = Boolean(currentCaseManagerId);
  const isCurrentCaseManagerInPool = hasCurrentCaseManager
    ? caseManagers.some((cm) => cm.id === currentCaseManagerId)
    : false;

  // Display value for the select - always a string for the Select component
  const displayValue: string = hasCurrentCaseManager
    ? (currentCaseManagerId as string)
    : 'unassigned';

  // Get display name for current case manager
  const getDisplayName = () => {
    if (!hasCurrentCaseManager) return null;
    return (
      currentCaseManager?.display_name ||
      (isCurrentCaseManagerInPool
        ? caseManagers.find((cm) => cm.id === currentCaseManagerId)
            ?.display_name
        : 'Not in pool')
    );
  };

  // Handle selection change - auto-call API
  const handleSelectChange = async (value: string) => {
    // Skip if same selection, placeholder value, or already updating (prevents race conditions)
    if (
      value === currentCaseManagerId ||
      value === 'unassigned' ||
      isUpdating
    ) {
      return;
    }

    setIsUpdating(true);
    const newCm = caseManagers.find((cm) => cm.id === value);
    const newCmName = newCm?.display_name || 'case manager';

    try {
      if (value === 'none') {
        // Remove case manager - revert tasks to managing paralegal
        await toastWithProgress(
          () =>
            removeCaseManager({
              matterId,
              data: { revert_tasks_to_mp: true },
            }).unwrap(),
          {
            loading: 'Removing case manager, reassigning tasks...',
            success: 'Case manager removed, tasks reassigned',
            error: 'Failed to remove case manager',
          },
        );
      } else if (!currentCaseManagerId) {
        // Assign new case manager
        // Use override if provided, otherwise default to the selected CM's case_auto_assign setting
        const autoAssignValue =
          autoTaskAssignmentOverride ?? newCm?.case_auto_assign ?? false;
        await toastWithProgress(
          () =>
            assignCaseManager({
              matterId,
              data: {
                case_manager_id: value,
                auto_task_assignment: autoAssignValue,
              },
            }).unwrap(),
          {
            loading: autoAssignValue
              ? `Assigning ${newCmName}, updating tasks...`
              : `Assigning ${newCmName}...`,
            success: (result) => {
              const count = result.tasks_reassigned;
              return count > 0
                ? `${newCmName} assigned, ${count} task${count === 1 ? '' : 's'} reassigned`
                : `${newCmName} assigned`;
            },
            error: 'Failed to assign case manager',
          },
        );
      } else {
        // Change case manager
        // Use override if provided, otherwise default to the selected CM's case_auto_assign setting
        const autoAssignValue =
          autoTaskAssignmentOverride ?? newCm?.case_auto_assign ?? false;
        await toastWithProgress(
          () =>
            changeCaseManager({
              matterId,
              data: {
                new_case_manager_id: value,
                auto_task_assignment: autoAssignValue,
              },
            }).unwrap(),
          {
            loading: autoAssignValue
              ? `Changing to ${newCmName}, updating tasks...`
              : `Changing to ${newCmName}...`,
            success: (result) => {
              const count = result.tasks_reassigned;
              return count > 0
                ? `Changed to ${newCmName}, ${count} task${count === 1 ? '' : 's'} reassigned`
                : `Changed to ${newCmName}`;
            },
            error: 'Failed to change case manager',
          },
        );
      }

      onSuccess?.();
    } catch {
      // Error already handled by toastWithProgress
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Select
      value={displayValue}
      onValueChange={handleSelectChange}
      disabled={disabled || isUpdating}
    >
      <SelectTrigger
        className={cn(
          'border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800',
          compact ? 'h-6 text-xs px-2 w-[140px]' : 'h-8 text-sm px-2.5 w-full',
        )}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          {!compact && (
            <User className="flex-shrink-0 text-zinc-400 h-3.5 w-3.5" />
          )}
          <SelectValue placeholder="Select...">
            {hasCurrentCaseManager ? (
              <span className="truncate font-medium text-zinc-900 dark:text-white">
                {getDisplayName()}
              </span>
            ) : (
              <span className="text-zinc-500">Select...</span>
            )}
          </SelectValue>
        </div>
      </SelectTrigger>
      <SelectContent className="min-w-[180px]">
        {/* Option to remove case manager if one is assigned */}
        {hasCurrentCaseManager && (
          <>
            <SelectItem
              value="none"
              className="text-red-600 dark:text-red-400 py-1.5 text-xs"
            >
              <div className="flex items-center gap-1.5">
                <UserMinus className="h-3.5 w-3.5" />
                <span>Remove</span>
              </div>
            </SelectItem>
            <div className="-mx-1 my-1 h-px bg-zinc-200 dark:bg-zinc-800" />
          </>
        )}

        {/* No case manager option for new assignments */}
        {!hasCurrentCaseManager && (
          <SelectItem value="unassigned" disabled className="py-1.5 text-xs">
            <span className="text-zinc-500">No case manager</span>
          </SelectItem>
        )}

        {/* Available case managers - compact list */}
        {caseManagers.map((cm) => (
          <SelectItem
            key={cm.id}
            value={cm.id}
            disabled={cm.id === currentCaseManagerId}
            className="py-1.5 text-xs"
          >
            <span className="font-medium">{cm.display_name}</span>
          </SelectItem>
        ))}

        {/* Show current case manager if not in pool */}
        {hasCurrentCaseManager &&
          !isCurrentCaseManagerInPool &&
          currentCaseManager && (
            <>
              <div className="-mx-1 my-1 h-px bg-zinc-200 dark:bg-zinc-800" />
              <SelectItem
                value={currentCaseManagerId as string}
                disabled
                className="opacity-60 py-1.5 text-xs"
              >
                <div className="flex flex-col">
                  <span className="font-medium">
                    {currentCaseManager.display_name}
                  </span>
                  <span className="text-[10px] text-amber-600 dark:text-amber-400">
                    Not in pool
                  </span>
                </div>
              </SelectItem>
            </>
          )}
      </SelectContent>
    </Select>
  );
};
